import { useEffect, useMemo, useState } from 'react';
import type { ExpandedTimeslot } from '../../utils/expand-timeslots';
import { TIME_SEGMENTS, getTimeSegment } from '../utils/booking-slots';
import type { TimeSegment } from '../utils/booking-slots';

interface UseTimeSegmentsReturn {
  timeSegment: TimeSegment
  setTimeSegment: (segment: TimeSegment) => void
  slotsBySegment: Record<TimeSegment, ExpandedTimeslot[]>
  /** Segments that have at least one slot, in day order. */
  activeSegments: TimeSegment[]
}

/**
 * Groups the day's slots into morning, afternoon and evening for the mobile
 * time picker. The chosen segment lives here, above the picker, so it is kept
 * while the time section is closed; it moves to the first segment with slots
 * when the chosen one has none.
 */
const useTimeSegments = (slots: ExpandedTimeslot[]): UseTimeSegmentsReturn => {
  const [timeSegment, setTimeSegment] = useState<TimeSegment>('afternoon');

  const slotsBySegment = useMemo(() => {
    const grouped: Record<TimeSegment, ExpandedTimeslot[]> = {
      morning: [],
      afternoon: [],
      evening: [],
    };
    slots.forEach((slot) => {
      const hour = parseInt(slot.start.split(':')[0], 10);
      grouped[getTimeSegment(hour)].push(slot);
    });
    return grouped;
  }, [slots]);

  const activeSegments = useMemo(
    () => TIME_SEGMENTS.filter((seg) => slotsBySegment[seg].length > 0),
    [slotsBySegment],
  );

  useEffect(() => {
    if (activeSegments.length > 0 && !activeSegments.includes(timeSegment)) {
      setTimeSegment(activeSegments[0]);
    }
  }, [activeSegments, timeSegment]);

  return {
    timeSegment, setTimeSegment, slotsBySegment, activeSegments,
  };
};

export default useTimeSegments;
